import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import { EyeIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import StatusBadge from "../../components/common/StatusBadge";
import { copyPublicUrl, formatDate } from "../../utils/HelperMethod";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "../../components/common/Pagination";
import SearchBar from "../../components/common/Search";
import CustomFilterDropdown from "../../components/common/CustomFilterDropdown";
import {
  useVendorExperiences,
  usePublicCategories,
} from "../../custom-hooks/queries";
import { AiOutlineEdit, AiOutlineEye, AiOutlineLink } from "react-icons/ai";
import ActionMenu from "../../components/common/ActionMenu";

export default function ExploreYourExperiencesPage() {
  const navigate = useNavigate();

  // State for filters and pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [searchText, setSearchText] = useState("");
  const pageSize = 10;

  // Status filter options
  const statusOptions = [
    { id: "draft", name: "Draft" },
    { id: "pending", name: "Pending" },
    { id: "approved", name: "Approved" },
    { id: "rejected", name: "Rejected" },
  ];

  // Fetch categories for filter
  const { data: categoriesData, isLoading: isLoadingCategories } =
    usePublicCategories();
  const categories = categoriesData?.data || categoriesData || [];

  // Map categories to filter options
  const categoryOptions = categories.map((cat) => ({
    id: cat.categoryId || cat.id,
    name: cat.name,
  }));

  // Fetch experiences from API
  const { data, isLoading, isError } = useVendorExperiences({
    page: currentPage,
    pageSize,
    status: statusFilter,
    category: categoryFilter,
    search: searchText,
  });

  // Map API response to table format
  const experiences =
    data?.data?.data?.map((item) => ({
      experienceId: item.experienceId,
      title: item.title,
      category: item.category,
      createdAt: item.createdAt,
      status: item.status,
      rejectionReason: item.rejectionReason,
      slug: item.slug,
    })) || [];

  const experienceActions = (row) => [
    {
      label: "View Experience",
      icon: <AiOutlineEye />,
      onClick: () => navigate(`/vendor/experience-details/${row.experienceId}`),
    },
    {
      label: "Edit Experience",
      icon: <AiOutlineEdit />,
      onClick: () =>
        navigate(`/vendor/experiences/new?experienceId=${row.experienceId}`),
    },
    {
      label: "Copy Experience URL",
      icon: <AiOutlineLink />,
      onClick: () => {
        copyPublicUrl(`/experiences/${row.slug}`);
      },
    },
  ];

  // Table columns
  const experienceColumns = [
    {
      key: "title",
      label: "EXPERIENCE NAME",
      width: "1.5fr",
      align: "left",
      render: (row) => (
        <div className="font-semibold text-gray-900">{row.title}</div>
      ),
    },
    {
      key: "category",
      label: "Category",
      width: "1.5fr",
      align: "left",
      render: (row) => (
        <div className="font-semibold text-gray-900">{row.category}</div>
      ),
    },
    {
      key: "createdAt",
      label: "DATE",
      width: "1.2fr",
      align: "center",
      render: (row) => (
        <div className="px-4 py-1 rounded-md bg-[#F5F5F5] text-gray-700 font-medium text-sm">
          {formatDate(row.createdAt)}
        </div>
      ),
    },
    {
      key: "status",
      label: "STATUS",
      width: "1fr",
      align: "center",
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "actions",
      label: "ACTIONS",
      width: "1fr",
      align: "center",
      render: (row) => <ActionMenu actions={experienceActions(row)} />,
    },
  ];

  // Handlers
  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (statusId, statusOption) => {
    setStatusFilter(statusId);
    setCurrentPage(1);
  };

  const handleCategoryChange = (categoryId, categoryOption) => {
    setCategoryFilter(categoryId);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="px-4 py-1 sm:px-6 sm:py-1">
      <PageHeader
        title="Explore Your Experiences"
        description="Discover and manage your experiences"
      />

      <div
        className="
          bg-white 
          rounded-[12px]
          border border-[#D9D9D9]
          shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.06)]
          overflow-hidden
          mt-4 sm:mt-6"
      >
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 py-6 sm:py-10 px-4 sm:px-6">
          <SearchBar onSearch={handleSearch} />
          <div className="flex flex-col sm:flex-row gap-3">
            <CustomFilterDropdown
              options={statusOptions}
              onChange={handleStatusChange}
              placeholder="Filter by Status"
              valueKey="id"
              labelKey="name"
            />
            <CustomFilterDropdown
              options={categoryOptions}
              onChange={handleCategoryChange}
              placeholder="Filter by Category"
              valueKey="id"
              labelKey="name"
            />
          </div>
        </div>

        {isError ? (
          <div className="flex justify-center items-center py-20 text-red-500">
            Failed to load experiences. Please try again.
          </div>
        ) : (
          <Table
            columns={experienceColumns}
            data={experiences}
            isLoading={isLoading}
            skeletonRows={pageSize}
          />
        )}
      </div>

      {!isLoading && !isError && data?.data?.totalPages > 1 && (
        <div className="mt-4 sm:mt-6">
          <Pagination
            currentPage={data?.data?.currentPage || currentPage}
            totalPages={data?.data?.totalPages || 1}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
}
